/**
 * Admin analytics dashboard schemas (#24).
 *
 * Shared between the API (`GET /api/admin/analytics`) and the admin SPA
 * (TanStack Query hooks + page-level types). Lives in `shared/` so a typo
 * on either side surfaces at compile time rather than at runtime.
 *
 * Spec references:
 *   - §14 Analytics — dashboard metrics + storage tiers (AnalyticsDaily for
 *     historic, AnalyticsEvent for last-7-days drill-downs).
 *   - §27 — operator-facing dashboards read aggregates whenever possible.
 *
 * Why a separate file from `analytics.ts`:
 *   - The public ingestion schema (`analytics.ts`) pins the wire shape of
 *     `POST /api/analytics/event`; coupling the admin dashboard schemas to
 *     it would force every public-beacon consumer to pull in dashboard
 *     types they never use.
 *   - The barrel re-exports both so callers see a flat surface.
 */

import { z } from "zod";

/**
 * Query-parameter schema for `GET /api/admin/analytics`.
 *
 * Field rules:
 *   - `siteId` — required; the dashboard is per-site (spec §14: each site
 *     is its own scope; rollup across sites is out of scope per the issue).
 *   - `from` / `to` — `YYYY-MM-DD` UTC dates. Both are inclusive of their
 *     calendar day on the SQL side (`from` lower-bound at 00:00:00 UTC,
 *     `to` upper-bound at end of day) — this matches operator
 *     expectations ("show me Jan 1 to Jan 31" means Jan 31 included). The
 *     service derives the exact instants.
 *   - `to` may equal `from` (single-day report). `to < from` → 422.
 *
 * `.strict()` rejects unknown keys so a typo (`form` vs `from`) surfaces
 * as a 422 instead of being silently coerced into "default last 30 days".
 */
export const adminAnalyticsQuerySchema = z
  .object({
    siteId: z.string().min(1).max(64),
    from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "from must be YYYY-MM-DD"),
    to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "to must be YYYY-MM-DD"),
  })
  .strict();
export type AdminAnalyticsQuery = z.infer<typeof adminAnalyticsQuerySchema>;

/**
 * One row of the daily-aggregate timeline. Mirrors `AnalyticsDaily` columns
 * the dashboard renders today; future columns can be added without breaking
 * existing readers. `date` is `YYYY-MM-DD` UTC for stable rendering across
 * time zones (operators read this in a browser; we don't depend on the
 * browser's locale to format the day).
 */
export type AdminAnalyticsDailyRow = {
  date: string;
  pageViews: number;
  uniqueVisitors: number;
  sessions: number;
  ctaClicks: number;
  contactFormSubmits: number;
  blogViews: number;
  productViews: number;
  outboundClicks: number;
  fileDownloads: number;
  botPageViews: number;
  aiScraperViews: number;
};

/**
 * Top-N pages over the requested range. Aggregated server-side from
 * `AnalyticsDaily.pageViews` so even multi-month ranges stay cheap.
 *
 * `path` is the page's current `Page.path` — pages renamed during the
 * range still surface under their current path; the join is by pageId.
 */
export type AdminAnalyticsTopPage = {
  pageId: string;
  path: string;
  totalViews: number;
};

/**
 * Bot vs human ratio over the requested range. Computed by summing
 * `pageViews` (humans) and `botPageViews` (bots) across daily rows; humans
 * is `pageViews - botPageViews` to avoid double-counting bots in the
 * "human" tile.
 */
export type AdminAnalyticsBotVsHuman = {
  humanViews: number;
  botViews: number;
};

/**
 * Per-UA bot scraper breakdown — last-7-days only, sourced from raw
 * `AnalyticsEvent` rows where `botType = 'AI_SCRAPER'`. Older events fall
 * out of the raw retention window (90 days default) and would be lossy to
 * report from `AnalyticsDaily` because that table's `aiScraperViews`
 * column doesn't carry the UA dimension (spec §14: aggregate is
 * intentionally coarse).
 */
export type AdminAnalyticsAiScraperRow = {
  ua: string;
  count: number;
};

/**
 * Top-10 referrers — last-7-days only, sourced from raw `AnalyticsEvent`.
 * `referrer` is the literal string the browser supplied; an empty string
 * surfaces as `(direct)` so the dashboard renders direct-traffic
 * meaningfully.
 */
export type AdminAnalyticsReferrerRow = {
  referrer: string;
  count: number;
};

/**
 * Response shape for `GET /api/admin/analytics`. Numbers are entirely
 * server-aggregated; the SPA does no math beyond formatting and computing
 * the simple total/bot ratio for the KPI tiles.
 *
 * - `daily` — full window, sourced from `AnalyticsDaily`.
 * - `topPages` — top 10 by sum(pageViews) over the window.
 * - `botVsHuman` — sum across the window.
 * - `aiScraperBreakdown` / `referrerBreakdown` — last-7-days only,
 *   sourced from raw `AnalyticsEvent` (per spec §14 segmentation rule).
 */
export type AdminAnalyticsResponse = {
  daily: AdminAnalyticsDailyRow[];
  topPages: AdminAnalyticsTopPage[];
  botVsHuman: AdminAnalyticsBotVsHuman;
  aiScraperBreakdown: AdminAnalyticsAiScraperRow[];
  referrerBreakdown: AdminAnalyticsReferrerRow[];
};
